import { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Flex, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { Avatar, Badge, Tooltip } from "@onengine/component-library";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  IconEye,
  IconLoader,
  IconPhone,
  IconPointFilled,
  IconProgressAlert,
} from "@tabler/icons-react";
import type { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import {
  normalizeNeedsAttentionSummaryKey,
  type DispatchQueueNeedsAttention,
  type DispatchQueueNeedsAttentionKey,
  type DispatchQueuePriorityBucket,
  type DispatchQueueRow,
} from "@/api/dispatch-queue/dispatch-queue.types";
import personIcon from "@/assets/Person.svg";
import Table from "@/components/ui/Table/Table";
import TableActionsMenu from "@/components/ui/TableActionsMenu/TableActionsMenu";
import type { TColumn } from "@/components/ui/TableCard/TableCard";
import {
  useDispatchQueueList,
  useDispatchQueueSummary,
} from "@/hooks/dispatch-queue/useDispatchQueueQueries";
import { useDispatchQueueWebSocket } from "@/hooks/dispatch-queue/useDispatchQueueWebSocket";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { tableSearchSchema, useTableUrlPersistence } from "@/hooks/useTableUrlPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getDispatchPriorityBadgeProps } from "@/utils/badgeConfig";
import { buildTableQueryOptions } from "@/utils/buildTableQueryOptions";
import { camelCaseToTitle } from "@/utils/camelCaseToTitle";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

import styles from "./dispatch-queue.module.scss";
import Loader from "@/components/ui/Loader/Loader";

export const Route = createFileRoute("/_authenticated/dispatch-queue/")({
  component: DispatchQueue,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const DANGER_ATTENTION_BADGE_KEYS = new Set<DispatchQueueNeedsAttentionKey>([
  "slaCritical",
  "openEscalations",
]);

const PRIORITY_TILE_CONFIG: {
  key: keyof DispatchQueuePriorityBucket;
  label: string;
}[] = [
  { key: "emergency", label: "Emergency" },
  { key: "sameDay", label: "Same Day" },
  { key: "nextDay", label: "Next Day" },
  { key: "2days+", label: "2 day+" },
];

const DEFAULT_TABLE_CARD_PROPS = {
  title: "Active Queue",
  showTableSettingsButton: false,
  showFiltersButton: true,
};

const INITIAL_TABLE_COLUMNS: TColumn[] = [
  { id: "workOrderNumber", label: "Work Order #", visible: true, sortable: true },
  { id: "priority", label: "Priority", visible: true, sortable: true },
  { id: "locationSiteId", label: "Location Site ID", visible: true, sortable: true },
  { id: "spTier", label: "SP Tier", visible: true, sortable: true },
  { id: "dispatchedTo", label: "Dispatched to", visible: true, sortable: true },
  { id: "dispatchStatus", label: "Dispatch Status", visible: true, sortable: true },
  { id: "remainingTime", label: "Remaining Time", visible: true, sortable: true },
  { id: "dispatcher", label: "Dispatcher", visible: true, sortable: true },
];

const DISPATCH_QUEUE_SORT_FIELD_MAP: Record<string, string> = {
  workOrderNumber: "workOrderNumber",
  priority: "priority",
  locationSiteId: "siteName",
  spTier: "currentTier",
  dispatchedTo: "assignedProviderName",
  dispatchStatus: "status",
  remainingTime: "slaExpiresAt",
  dispatcher: "dispatcherName",
};

function getDispatchQueueSortByField(columnId: string): string | undefined {
  return DISPATCH_QUEUE_SORT_FIELD_MAP[columnId];
}

function getAttentionBadgeColor(key: string): "danger" | "warning" {
  return DANGER_ATTENTION_BADGE_KEYS.has(key as DispatchQueueNeedsAttentionKey)
    ? "danger"
    : "warning";
}

function toFinite(n: number): number | null {
  return Number.isFinite(n) ? n : null;
}

/**
 * Approximate minutes from remaining-time labels (e.g. "~ 5 days", "5 days", "~ 2hr 30min", "~ 55 min", "4:32").
 * Colon-separated values use a small-table heuristic (documented at parse site). Unrecognized labels return null.
 */
function parseRemainingTimeToMinutes(value: string): number | null {
  const UNICODE_TILDES = /^[∼～\uFF5E\u301C~]\s*/u;
  const TIME_UNITS =
    /^(?:(?<days>[\d.]+)\s*days?|(?:(?<hr>[\d.]+)\s*hr)?\s*(?:(?<min>[\d.]+)\s*min)?)$/i;
  const COLON_FORMAT = /^(\d{1,2}):(\d{2})$/;

  const normalized = value.trim().replace(UNICODE_TILDES, "");
  if (!normalized) return null;

  const unitMatch = normalized.match(TIME_UNITS);

  if (unitMatch?.groups) {
    const { days, hr, min } = unitMatch.groups;
    if (days) return toFinite(Number(days) * 1440);
    if (hr || min) return toFinite(Number(hr ?? 0) * 60 + Number(min ?? 0));
  }

  const colonMatch = normalized.match(COLON_FORMAT);
  if (colonMatch) {
    const [hours, minutes] = [Number(colonMatch[1]), Number(colonMatch[2])];
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) return null;
    return hours > 15 ? hours * 60 + minutes : hours + minutes / 60;
  }

  return null;
}

function getRemainingTimeTextClassName(minutes: number): string {
  if (minutes <= 30) return styles.remainingTimeDanger;
  if (minutes <= 55) return styles.remainingTimeAccentOrange;
  if (minutes < 1440) return styles.remainingTimeWarning;
  return styles.remainingTimeSuccess;
}

function parseDispatchStatusCountdown(status: string): { base: string; countdown: string | null } {
  const match = status.match(/^(Waiting for Response:\s*)(\d+:\d+\s*left)$/i);
  if (!match) {
    return { base: status, countdown: null };
  }
  return { base: match[1], countdown: match[2] };
}

function DispatchQueueAttentionBar({
  needsAttention,
  selectedKey,
  onSelectKey,
}: {
  needsAttention?: DispatchQueueNeedsAttention;
  selectedKey: string | null;
  onSelectKey: (key: string) => void;
}) {
  const items = Object.entries(needsAttention ?? {})
    .map(([key, count]) => {
      const summaryKey = normalizeNeedsAttentionSummaryKey(key);
      if (!summaryKey) return null;

      return {
        key: summaryKey,
        label: camelCaseToTitle(summaryKey),
        count,
        color: getAttentionBadgeColor(summaryKey),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <Box className={styles.needsAttentionWrapper}>
      <Box className={styles.needsAttentionBar}>
        <Text className={styles.needsAttentionLabel}>Needs Attention:</Text>
        {items.map((item) => {
          const isSelected = selectedKey === item.key;

          return (
            <UnstyledButton
              key={item.key}
              type="button"
              className={styles.needsAttentionBadgeButton}
              onClick={() => onSelectKey(item.key)}
              aria-pressed={isSelected}
            >
              <Badge
                size="small"
                color={item.color}
                variant="outline"
                className={
                  isSelected
                    ? item.color === "danger"
                      ? styles.needsAttentionBadgeSelectedDanger
                      : styles.needsAttentionBadgeSelectedWarning
                    : undefined
                }
              >
                {`${item.label} \u2022 ${item.count}`}
              </Badge>
            </UnstyledButton>
          );
        })}
      </Box>
    </Box>
  );
}

function DispatchQueuePriorityTiles({
  priorityBucket,
  isLoading = false,
  selectedKey,
  onSelectKey,
}: {
  priorityBucket?: DispatchQueuePriorityBucket;
  isLoading?: boolean;
  selectedKey: keyof DispatchQueuePriorityBucket | null;
  onSelectKey: (key: keyof DispatchQueuePriorityBucket) => void;
}) {
  const tiles = PRIORITY_TILE_CONFIG.map(({ key, label }) => ({
    key,
    label,
    count: priorityBucket?.[key] ?? 0,
  }));

  return (
    <Box className={styles.priorityTilesGrid}>
      {tiles.map((tile) => {
        const isSelected = selectedKey === tile.key;

        return (
          <UnstyledButton
            key={tile.key}
            type="button"
            className={styles.priorityTileButton}
            onClick={() => onSelectKey(tile.key)}
            aria-pressed={isSelected}
          >
            <Box
              className={`${styles.priorityTile} ${isSelected ? styles.priorityTileSelected : ""}`}
            >
              <Text className={styles.priorityTileLabel}>{tile.label}</Text>
              <Text className={styles.priorityTileCount}>{isLoading ? "\u2014" : tile.count}</Text>
            </Box>
          </UnstyledButton>
        );
      })}
    </Box>
  );
}

function DispatchedToCell({ row }: { row: DispatchQueueRow }) {
  if (!row.dispatchedTo) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  if (row.dispatchedTo === "Providers Exhausted") {
    return (
      <Flex align="center" gap={4} wrap="nowrap" className={styles.exhaustedDispatchedToLink}>
        <IconProgressAlert size={16} color="var(--icon-danger-default)" />
        <Text size="sm" className={styles.exhaustedDispatchedToLink}>
          Providers Exhausted
        </Text>
      </Flex>
    );
  }

  if (row.dispatchedTo.includes("matching providers")) {
    return (
      <Text size="sm" className={styles.matchingDispatchedToLink}>
        {row.dispatchedTo}
      </Text>
    );
  }

  const countLabel =
    row.dispatchedToCount !== undefined
      ? row.dispatchedToTotal !== undefined
        ? `${row.dispatchedToCount}/${row.dispatchedToTotal}`
        : `${row.dispatchedToCount}`
      : "";

  return (
    <Group gap={4} align="center">
      {countLabel && (
        <>
          <Text size="xs" c="var(--text-brand-default)">
            {countLabel}
          </Text>
          <Text size="sm" c="var(--text-brand-default)">
            &bull;
          </Text>
        </>
      )}
      <Text size="sm" className={styles.dispatchedToLink}>
        {row.dispatchedTo}
      </Text>
      {row.dispatchedToRankBadge && (
        <Badge
          size="small"
          color="neutral"
          variant="outline"
          className={styles.dispatchedToRankBadge}
        >
          {row.dispatchedToRankBadge}
        </Badge>
      )}
    </Group>
  );
}

function RemainingTimeCell({ row }: { row: DispatchQueueRow }) {
  const value = row.remainingTime;
  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  const minutes = parseRemainingTimeToMinutes(value);

  if (minutes !== null && minutes <= 15) {
    return (
      <Badge size="small" color="danger" variant="lowContrast">
        {value}
      </Badge>
    );
  }

  const className =
    minutes === null ? styles.remainingTimeDefault : getRemainingTimeTextClassName(minutes);

  return (
    <Text size="sm" className={className} component="span">
      {value}
    </Text>
  );
}

function DispatchStatusCell({ row }: { row: DispatchQueueRow }) {
  const status = row.dispatchStatus;
  const showPhone = status.startsWith("Contacting");
  const showSpinner = !showPhone;
  const { base, countdown } = parseDispatchStatusCountdown(status);

  return (
    <Group gap={6} align="center" wrap="nowrap">
      {showSpinner && <IconLoader color="var(--icon-brand-default)" size={14} />}
      {showPhone && <IconPhone size={14} />}
      {countdown === null ? (
        <Text size="sm" component="span">
          {base}
        </Text>
      ) : (
        <Group gap={4} align="baseline" wrap="nowrap">
          <Text size="sm" component="span">
            {base}
          </Text>
          <Text size="sm" c="dimmed" component="span">
            {countdown}
          </Text>
        </Group>
      )}
    </Group>
  );
}

function PersonAssignmentCell({ name }: { name: string }) {
  const isUnassigned = !name || name === "Not assigned";

  if (isUnassigned) {
    return (
      <Group gap="var(--spacing-x-small)" align="center" wrap="nowrap">
        <Avatar type="person" size="xs" />
        <Text size="sm" className={styles.notAssignedLabel} c="dimmed">
          Not assigned
        </Text>
      </Group>
    );
  }

  return (
    <Group gap="var(--spacing-x-small)" align="center" wrap="nowrap">
      <Avatar src={personIcon} type="photo" name={name} size="xs" />
      <Text size="sm">{name}</Text>
    </Group>
  );
}

function DispatchQueueActiveTable({
  refetchInterval,
  needsAttentionKey,
  priorityKey,
}: {
  refetchInterval?: number | false;
  needsAttentionKey: DispatchQueueNeedsAttentionKey | null;
  priorityKey: keyof DispatchQueuePriorityBucket | null;
}) {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/dispatch-queue/" });
  const [tableColumns, setTableColumns] = useState<TColumn[]>(INITIAL_TABLE_COLUMNS);

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    setSearchValue,
    debouncedSearch,
    sorting,
    setSorting,
    filters,
    filtersKey,
    viewName,
    applyFilters,
    resetFilters,
  } = useTableUrlPersistence({
    viewName: "dispatchQueueList",
    filterKeyToLabel: {},
    searchParams,
    navigate,
    tableColumns,
  });

  const trimmedSearch = debouncedSearch.trim();

  // Reset to first page whenever the active filter changes so the user
  // doesn't land on an out-of-range page after narrowing results.
  useEffect(() => {
    setPage(1);
  }, [needsAttentionKey, priorityKey, setPage]);

  const queryOptions = useMemo(
    () =>
      buildTableQueryOptions({
        page,
        pageSize,
        search: trimmedSearch,
        sorting,
        filters,
        tableColumns,
        getSortByField: getDispatchQueueSortByField,
      }),
    [page, pageSize, trimmedSearch, sorting, filtersKey, tableColumns],
  );

  const listOptions = useMemo(
    () => ({
      page: queryOptions.page,
      pageSize: queryOptions.pageSize,
      search: queryOptions.search,
      sortBy: queryOptions.sortBy,
      sortOrder: queryOptions.sortOrder,
      needsAttention: needsAttentionKey ? [needsAttentionKey] : undefined,
      priority: priorityKey ? [priorityKey] : undefined,
    }),
    [queryOptions, needsAttentionKey, priorityKey],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((prevSorting) => {
        const newSorting =
          typeof updaterOrValue === "function" ? updaterOrValue(prevSorting) : updaterOrValue;

        if (JSON.stringify(prevSorting) !== JSON.stringify(newSorting)) {
          setPage(1);
        }

        return newSorting;
      });
    },
    [setSorting, setPage],
  );

  const handleSortChange = (columnId: string | null, direction: "asc" | "desc") => {
    if (columnId) {
      setSorting([{ id: columnId, desc: direction === "desc" }]);
      setPage(1);
    } else {
      setSorting([]);
      setPage(1);
    }
  };

  const {
    data: listData,
    isPending,
    isFetching,
  } = useDispatchQueueList(listOptions, refetchInterval);

  const tableData = listData?.data ?? [];
  const totalRecords = listData?.pagination.total ?? 0;
  const isLoading = isPending;
  const isRefetching = isFetching && !isPending;

  const columns = useMemo<ColumnDef<DispatchQueueRow, unknown>[]>(
    () => [
      {
        accessorKey: "workOrderNumber",
        header: "Work Order #",
        cell: ({ row, getValue }) => {
          const workOrderNumber = getValue() as string;

          return (
            <UnstyledButton
              type="button"
              className={styles.workOrderLinkButton}
              onClick={() => {
                navigate({
                  to: "/work-orders/$workOrderId",
                  params: { workOrderId: row.original.id },
                });
              }}
            >
              <Text size="sm" className={styles.workOrderLink}>
                {workOrderNumber}
              </Text>
            </UnstyledButton>
          );
        },
      },
      {
        accessorKey: "priority",
        header: "Priority",
        cell: ({ getValue }) => {
          const priority = getValue() as string;
          if (!priority) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }

          const badgeProps = getDispatchPriorityBadgeProps(priority);
          return (
            <Badge size="medium" color={badgeProps.color} variant={badgeProps.variant}>
              <Flex align="center" gap="var(--spacing-4x-small)">
                <IconPointFilled size={16} />
                {priority}
              </Flex>
            </Badge>
          );
        },
      },
      {
        accessorKey: "locationSiteId",
        header: "Location Site ID",
        cell: ({ row, getValue }) => {
          const locationSiteId = getValue() as string;
          const address = row.original.address?.trim() ?? "";

          if (!address) {
            return (
              <Text size="sm" className={styles.locationLink}>
                {locationSiteId}
              </Text>
            );
          }

          const tooltipContent = (
            <Stack gap="var(--spacing-3x-small)">
              <Text size="sm" fw={700}>
                {locationSiteId}
              </Text>
              <Text size="sm">{address}</Text>
            </Stack>
          );

          return (
            <Tooltip content={tooltipContent} pointer="top-center">
              <Box style={{ width: "fit-content" }}>
                <Text size="sm" className={styles.locationLink}>
                  {locationSiteId}
                </Text>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "spTier",
        header: "SP Tier",
        cell: ({ row }) => {
          const spTier = row.original.spTier;
          if (!spTier) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }

          return (
            <Badge size="small" color="neutral" variant="outline">
              {spTier}
            </Badge>
          );
        },
      },
      {
        accessorKey: "dispatchedTo",
        header: "Dispatched to",
        cell: ({ row }) => <DispatchedToCell row={row.original} />,
      },
      {
        accessorKey: "dispatchStatus",
        header: "Dispatch Status",
        cell: ({ row }) => <DispatchStatusCell row={row.original} />,
      },
      {
        accessorKey: "remainingTime",
        header: "Remaining Time",
        cell: ({ row }) => <RemainingTimeCell row={row.original} />,
      },
      {
        accessorKey: "dispatcher",
        header: "Dispatcher",
        cell: ({ getValue }) => <PersonAssignmentCell name={getValue() as string} />,
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => (
          <Box className={styles.actionsCell}>
            <TableActionsMenu
              dotOrientation="horizontal"
              options={[
                {
                  btnText: "View Details",
                  onClickHandler: () => {
                    navigate({
                      to: "/work-orders/$workOrderId",
                      params: { workOrderId: row.original.id },
                    });
                  },
                  iconLeft: <IconEye size={20} />,
                },
              ]}
            />
          </Box>
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [navigate],
  );

  const tableTitle = isRefetching ? (
    <Group gap="var(--spacing-x-small)" align="center" wrap="nowrap">
      Active Queue
      <Loader size="small" withContainer={false} />
    </Group>
  ) : (
    DEFAULT_TABLE_CARD_PROPS.title
  );

  return (
    <Flex direction="column" gap="var(--spacing-medium)">
      <Table
        data={tableData}
        columns={columns}
        enableSorting
        manualSorting
        sorting={sorting}
        onSortingChange={handleSortingChange}
        manualPagination
        pageCount={Math.ceil(totalRecords / pageSize) || 1}
        pageIndex={page - 1}
        totalRecords={totalRecords}
        onPageChange={(newPageIndex) => setPage(newPageIndex + 1)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
        }}
        pageSize={pageSize}
        isLoading={isLoading}
        showTableCard
        columnPinning={{
          right: ["actions"],
        }}
        tableCardProps={{
          ...DEFAULT_TABLE_CARD_PROPS,
          title: tableTitle,
          columns: tableColumns,
          onColumnsChange: setTableColumns,
          onSearchChange: setSearchValue,
          onSortChange: handleSortChange,
          initialSearchValue: searchParams.search ?? "",
          viewName,
          filterData: {},
          onApplyFilters: applyFilters,
          onResetFilters: resetFilters,
          isLoading,
        }}
      />
    </Flex>
  );
}

function DispatchQueue() {
  const { pollingInterval } = useDispatchQueueWebSocket();
  const { data: summary, isPending: isSummaryLoading } = useDispatchQueueSummary(pollingInterval);
  const [selectedAttentionKey, setSelectedAttentionKey] =
    useState<DispatchQueueNeedsAttentionKey | null>(null);
  const [selectedPriorityKey, setSelectedPriorityKey] = useState<
    keyof DispatchQueuePriorityBucket | null
  >(null);

  const handleAttentionKeyToggle = (key: string) => {
    const attentionKey = normalizeNeedsAttentionSummaryKey(key);
    if (!attentionKey) return;

    setSelectedAttentionKey((prev) => (prev === attentionKey ? null : attentionKey));
  };

  const handlePriorityKeyToggle = (key: keyof DispatchQueuePriorityBucket) => {
    setSelectedPriorityKey((prev) => (prev === key ? null : key));
  };

  useBreadcrumb([
    {
      title: "Dispatch Queue",
      href: "/dispatch-queue",
      icon: getRouteIcon(MODULE_KEYS_MAP.dispatchQueue),
    },
  ]);

  return (
    <Flex direction="column" className={styles.container} gap="var(--spacing-large)">
      <DispatchQueueAttentionBar
        needsAttention={summary?.needsAttention}
        selectedKey={selectedAttentionKey}
        onSelectKey={handleAttentionKeyToggle}
      />
      <DispatchQueuePriorityTiles
        priorityBucket={summary?.priorityBucket}
        isLoading={isSummaryLoading}
        selectedKey={selectedPriorityKey}
        onSelectKey={handlePriorityKeyToggle}
      />
      <DispatchQueueActiveTable
        refetchInterval={pollingInterval}
        needsAttentionKey={selectedAttentionKey}
        priorityKey={selectedPriorityKey}
      />
    </Flex>
  );
}
